import {IUserModel} from "../user-model.interface";
import {IServiceFactory} from "../../../service-factory.interface";
import {IDotRezUserSession} from "../../../dot-rez-api/session/user-session/dot-rez-user.session.interface";
import {DotRezUserSession} from "../../../dot-rez-api/session/user-session/dot-rez-user.session";
import {IReactionDisposer, makeObservable, observable, reaction, runInAction} from "mobx";
import {IDotRezBookingSession} from "../../../dot-rez-api/session/booking-session/dot-rez-booking.session.interface";
import {DotRezBookingSession} from "../../../dot-rez-api/session/booking-session/dot-rez-booking.session";
import {VoidDotRezSessionTimer} from "../../../dot-rez-api/session/session-timer/void-dot-rez-session-timer";
import {NullableString} from "../../../../types/nullable-types";
import {WellKnownQueryParams} from "../../../window/window.service.interface";
import {
    SpecialPriceMarketUserAgreement,
    SpecialPriceMarketUserOptions
} from "../../../flight-search/flight-search-controller/flight-search-params.interface";

export class AnonymousUserModel implements IUserModel {
    constructor(public readonly services: IServiceFactory) {
        this._getSessionPromise = this._createUserSession();
        makeObservable<this, '_getSessionPromise'>(this, {
            _getSessionPromise: observable.ref
        });

        this._reactions.push(reaction(() => this.currentAgentName,
            async () => {
                await this.reCreateSession();
            }));
    }

    private _reactions: IReactionDisposer[] = [];

    private _getSessionPromise: Promise<IDotRezUserSession>;
    getSession(): Promise<IDotRezUserSession> {
        return this._getSessionPromise;
    }

    get currentAgentName() : NullableString {
        const searchController = this.services.flightSearch.searchController;
        const specialPriceMarket = searchController.getSpecialPriceMarket();
        const defaultAgentName = this.services.window.getUrlParam(WellKnownQueryParams.Agent) ?? null;

        if(!specialPriceMarket) {
            return defaultAgentName;
        }

        if(searchController.fields.specialPriceMarketUserOption.value === SpecialPriceMarketUserOptions.None) {
            return defaultAgentName;
        }

        if(searchController.fields.specialPriceMarketUserAgreement.value === SpecialPriceMarketUserAgreement.DontAgree) {
            return defaultAgentName;
        }


        if(searchController.fields.specialPriceMarketUserOption.value === SpecialPriceMarketUserOptions.Regular) {
            const agent = specialPriceMarket.getAgent(false);
            if(agent) {
                return agent;
            }
        } else if(searchController.fields.specialPriceMarketUserOption.value === SpecialPriceMarketUserOptions.Season) {
            const agent = specialPriceMarket.getAgent(true);
            if(agent) {
                return agent;
            }
        }

        return defaultAgentName;
    }

    private async _createDotRezToken(): Promise<string> {
        if(this.currentAgentName) {
            try {
                const dotRezToken = await this.services.dotRezApi.getDeepLinkAgentToken({
                    agentName: this.currentAgentName
                });

                if(dotRezToken) {
                    return dotRezToken;
                }

            } catch (err) {
                this.services.logger.error(`Failed to create token for agent ${this.currentAgentName}`, err);
            }

        }
        return await this.services.dotRezApi.createAnonymousToken()
    }

    private async _createUserSession(): Promise<IDotRezUserSession> {
        const token = await this._createDotRezToken();
        return new DotRezUserSession(token, this.services);
    }

    reCreateSession(): Promise<IDotRezUserSession> {
        const promise = this._createUserSession();
        runInAction(() => {
            this._getSessionPromise = promise;
        });

        return promise;
    }

    async createTransientBookingSession(): Promise<IDotRezBookingSession> {
        const token = await this._createDotRezToken();

        return new DotRezBookingSession(token, this.services, new VoidDotRezSessionTimer());
    }

    dispose(): void {
        this._reactions.forEach(r => r());
    }
}
